import React from "react";

import { clsx } from "clsx";

import { Lock } from "lucide-react";

import { twMerge } from "@/design-system/twMerge";

export type CardAccent = "none" | "critical" | "warning" | "success" | "info";

const accentClasses: Record<Exclude<CardAccent, "none">, string> = {
  critical: "border-l-4 border-l-status-error",
  warning: "border-l-4 border-l-status-warning",
  success: "border-l-4 border-l-status-success",
  info: "border-l-4 border-l-status-info",
};

interface CardProps {
  className?: string;
  containerClassName?: string;
  headerText?: string;
  headerClassName?: string;
  descriptionText?: string;
  icon?: React.ReactNode;
  headerAction?: React.ReactNode;
  locked?: boolean;
  children?: React.ReactNode;
  onClick?: () => void;
  clickable?: boolean;
  accent?: CardAccent;
}

export function CardSkeleton() {
  return (
    <div className="border-borderSubtle bg-surface-default shadow-elevation-100 flex w-full flex-col gap-5 rounded-lg border p-5">
      <div className="flex items-center gap-3">
        <div className="bg-fill-neutral h-12 w-12 min-w-12 animate-pulse rounded" />
        <div className="flex w-full justify-between gap-2">
          <div className="flex-1">
            <div className="bg-fill-neutral mb-2 h-6 animate-pulse rounded" />
            <div className="bg-fill-neutral h-4 animate-pulse rounded" />
          </div>
        </div>
      </div>
      <div className="pb-2">
        <div className="bg-fill-neutral h-9 w-24 animate-pulse rounded" />
      </div>
    </div>
  );
}

export function Card({
  className,
  containerClassName,
  headerText,
  headerClassName,
  descriptionText,
  icon,
  headerAction,
  locked,
  children,
  onClick,
  clickable,
  accent = "none",
}: CardProps) {
  const isClickable = clickable || !!onClick;
  const hasHeader =
    !!headerText || !!descriptionText || !!icon || !!headerAction || !!locked;

  return (
    <div
      className={twMerge(
        clsx(
          "border-borderSubtle bg-surface-default shadow-elevation-100 flex w-full flex-col justify-between gap-5 rounded-lg border p-5",
          children && "pb-2",
          isClickable &&
            "hover:bg-surface-hover-subtle focus-visible:ring-interactive-selected cursor-pointer transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
          accent !== "none" && accentClasses[accent],
          className,
        ),
      )}
      onClick={onClick}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (!isClickable) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      {hasHeader && (
        <div
          className={twMerge(
            clsx("flex items-center gap-3", containerClassName),
          )}
        >
          {icon}
          <div className="flex w-full justify-between gap-2">
            <div>
              {headerText && (
                <h4
                  className={twMerge(
                    "text-heading-md line-clamp-2",
                    headerClassName,
                  )}
                >
                  {headerText}
                </h4>
              )}
              {descriptionText && (
                <span className="text-body-sm text-textSubtle">
                  {descriptionText}
                </span>
              )}
            </div>
            {(locked || headerAction) && (
              <div className="flex items-center gap-2 self-start">
                {locked && <Lock size={20} className="text-iconMinimal" />}
                {headerAction}
              </div>
            )}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

interface CardActionsWrapperProps {
  children: React.ReactNode;
  className?: string;
}

export function CardActionsWrapper({
  children,
  className,
}: CardActionsWrapperProps) {
  return (
    <div className={twMerge("flex items-center justify-end gap-2", className)}>
      {children}
    </div>
  );
}
